import axios from "axios";

export const AUTH_TOKEN_STORAGE_KEY = "rm_auth_token";
export const LOCATION_STORAGE_KEY = "rm_current_location_id";
export const ORGANIZATION_STORAGE_KEY = "rm_current_organization_id";

// In-memory token store (fallback for browsers that block third-party cookies)
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  // Sesión nueva: se olvida el "ya me curé una vez", para que la próxima vez
  // que pase de verdad vuelva a recargar.
  if (token) sessionStorage.removeItem("rm_stale_org_recovered");
}

export function getAuthToken(): string | null {
  return authToken;
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:4000/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send cookies with requests (cross-domain auth)
});

// Request interceptor for adding location and organization headers
apiClient.interceptors.request.use(
  (config) => {
    // Add Bearer token as fallback (for browsers blocking third-party cookies)
    if (authToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // Add current location ID to all requests
    const locationId = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (locationId) {
      config.headers["X-Location-Id"] = locationId;
    }

    // Add organization override for superadmin context switching
    const organizationId = localStorage.getItem(ORGANIZATION_STORAGE_KEY);
    if (organizationId) {
      config.headers["X-Organization-Id"] = organizationId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling errors. CompleteRegistration (Meta) se
// dispara en Register.tsx: hacerlo también aquí contaba cada alta dos veces.
/**
 * Bandera de un solo uso para no entrar en un bucle de recargas si, por lo que
 * sea, el problema persiste después de limpiar.
 */
const STALE_ORG_FLAG = "rm_stale_org_recovered";

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // El negocio que teníamos seleccionado ya no existe (se borró estando el
    // panel abierto). La cabecera X-Organization-Id viaja en TODAS las
    // peticiones, así que todas fallaban a la vez... incluida la que lista los
    // locales para poder elegir otro. Sin lista no se limpiaba la selección:
    // el panel se quedaba muerto hasta vaciar el navegador a mano.
    // Aquí se cura solo: se olvida el negocio muerto y se recarga UNA vez.
    if (
      error.response?.status === 404 &&
      error.response?.data?.code === "ORGANIZATION_NOT_FOUND" &&
      localStorage.getItem(ORGANIZATION_STORAGE_KEY)
    ) {
      localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
      localStorage.removeItem(LOCATION_STORAGE_KEY);
      if (!sessionStorage.getItem(STALE_ORG_FLAG)) {
        sessionStorage.setItem(STALE_ORG_FLAG, "1");
        window.location.reload();
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page or making auth requests
      const isAuthRequest = error.config?.url?.startsWith("/auth/");
      const isOnLoginPage = window.location.pathname === "/login";

      if (!isAuthRequest && !isOnLoginPage) {
        // Handle unauthorized - redirect to login
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
